import { useMemo, useState } from "react";

import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

import { useGetSubscriptionPlanQuery } from "@/app/redux/features/api/common";
import { useGetBotbPassMembersDataQuery } from "@/app/redux/features/api/membersHome";
import { useCompetition } from "@/app/redux/features/categories/selectors";
import { formatCurrency } from "@/app/(general-header)/botb-rewards/utils";
import { EntryAllocation } from "../types";
import EntryCard from "./EntryCard";
import BenefitsModal from "@/app/shared-components/BenefitsModal";

const ENTRY_CARD_CONFIG: { featureKeyword: string; icon: string; description: string }[] = [
  {
    featureKeyword: "Dream Home",
    icon: "/assets/images/botb-pass-members/icon-house.png",
    description: "to win a **$HC_TITLE**",
  },
  {
    featureKeyword: "Luxury Cars",
    icon: "/assets/images/botb-pass-members/icon-car.png",
    description: "to win **Luxury Cars**",
  },
  {
    featureKeyword: "Dream Car",
    icon: "/assets/images/botb-pass-members/icon-racing-car.png",
    description: "to pick your **Dream Car**",
  },
  {
    featureKeyword: "Lifestyle",
    icon: "/assets/images/botb-pass-members/icon-tech.png",
    description: "to choose from **Watches, Cars, Tech & more**",
  },
  {
    featureKeyword: "Instant Win",
    icon: "/assets/images/botb-pass-members/icon-lightning.png",
    description: "for **Instant Cash** prizes",
  },
];

const EXCLUSIVE_BONUS_DEFAULTS = {
  icon: "/assets/images/botb-pass-members/icon-diamond.png",
  count: "Daily entries",
  isExclusiveBonus: true as const,
};

export default function PlayingForSection() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { data: subscriptionPlan, isLoading } = useGetSubscriptionPlanQuery();
  const { data: membersData } = useGetBotbPassMembersDataQuery();
  const houseCompetition = useCompetition({ CompetitionTypePrefix: "HC" });

  const planName = subscriptionPlan?.name ?? "";
  const basePrice = subscriptionPlan?.prices?.[0]?.metadata["base_price"] ?? "";
  const planPrice = subscriptionPlan?.amount ? formatCurrency(subscriptionPlan.amount / 100) : "";

  const hcTitle = houseCompetition?.Title ?? "";

  const entries: EntryAllocation[] = useMemo(() => {
    const features = subscriptionPlan?.features ?? [];
    if (features.length === 0) return [];

    const mapped = ENTRY_CARD_CONFIG.map(({ featureKeyword, icon, description }) => {
      const match = features.find(f => f.toLowerCase().includes(featureKeyword.toLowerCase()));
      const firstTwoWords = match ? match.split(/\s+/).slice(0, 2).join(" ").replace(/,$/, "") : "0 entries";

      return {
        icon,
        count: firstTwoWords,
        description: description.replace("$HC_TITLE", hcTitle),
      };
    });

    const meta: Record<string, string> = Object.fromEntries(subscriptionPlan?.metadata ?? []);
    const scEntries = meta["SC"] ?? "0";

    const exclusiveCard: EntryAllocation = {
      ...EXCLUSIVE_BONUS_DEFAULTS,
      count: `${scEntries} entries/day`,
      description: membersData?.SubscriberCardDescription || "exclusive daily prizes",
      exclusiveTag: membersData?.SubscriberCardTag,
      exclusiveTitle: membersData?.SubscriberCardTitle,
      exclusiveDescription: membersData?.SubscriberCardDescription,
    };

    return [...mapped, exclusiveCard];
  }, [subscriptionPlan?.features, subscriptionPlan?.metadata, hcTitle, membersData]);

  if (isLoading) {
    return (
      <section className="w-full px-4 lg:px-6 py-10 lg:py-[104px] overflow-hidden">
        <div className="flex flex-col items-center max-w-[1340px] mx-auto gap-6 lg:gap-8">
          <div className="flex flex-col items-center text-center w-full max-w-[320px] lg:max-w-lg gap-2 lg:gap-3">
            <Skeleton height={40} width={240} borderRadius={8} />
            <Skeleton height={24} containerClassName="w-full block" borderRadius={6} />
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-3 xl:flex xl:flex-wrap w-full gap-x-2 gap-y-3 lg:gap-3 lg:justify-between">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-[110px] lg:h-[152px] lg:flex-1 lg:min-w-[160px]">
                <Skeleton height="100%" borderRadius={12} containerClassName="block w-full h-full" />
              </div>
            ))}
          </div>

          <Skeleton height={20} width={60} borderRadius={4} />
        </div>
      </section>
    );
  }

  return (
    <section className="w-full px-4 lg:px-6 py-10 lg:py-[104px] overflow-hidden">
      <div className="flex flex-col items-center max-w-[1340px] mx-auto gap-6 lg:gap-8">
        <div className="flex flex-col items-center text-center gap-2 lg:gap-3">
          <h2 className="!m-0 p-0 normal-case text-[24px] lg:text-[32px] font-bold font-jost leading-7.5 lg:leading-10 text-dark-1">
            {`What you're `}
            <span className="text-primary">playing for</span>
            <br className="block lg:hidden" />
            {` this month`}
          </h2>
          {planName && (
            <p className="m-0 p-0 text-base lg:text-xl font-normal font-jost leading-5.5 lg:leading-6.5 text-secondary">
              Your {planName} Pass — worth over {basePrice} in tickets for only {planPrice}
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-3 xl:flex xl:flex-wrap w-full gap-x-2 gap-y-3 lg:gap-3 lg:justify-between">
          {entries.map((entry, index) => (
            <EntryCard key={index} {...entry} />
          ))}
        </div>

        <button
          onClick={() => setIsModalOpen(true)}
          aria-label="View pass details"
          className="flex items-center gap-0.5 cursor-pointer">
          <span className="text-sm lg:text-base font-medium font-jost leading-5 lg:leading-5.5 text-primary underline">
            Details
          </span>
          <svg width="16" height="16" viewBox="0 0 15 15" fill="none" aria-hidden="true" className="shrink-0">
            <path
              d="M5.5 3.5L10 7.5L5.5 11.5"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-primary"
            />
          </svg>
        </button>
      </div>

      <BenefitsModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </section>
  );
}
